import React, { useEffect, useState, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import SessionCard from './SessionCard';
import useAuth from '../hooks/useAuth';
import { catalystFetch } from '../services/catalystFetch';

const API_BASE = process.env.NODE_ENV === 'production'
  ? '/server/processMedia'
  : '';

/** Compact weekly-quota banner. Tints toward warning/danger as usage climbs. */
function UsageBanner({ usage }) {
  const { analysisCount, minutesUsed, caps, resetAtMs } = usage;
  const aPct = caps.weeklyAnalyses ? analysisCount / caps.weeklyAnalyses : 0;
  const mPct = caps.weeklyMinutes ? minutesUsed / caps.weeklyMinutes : 0;
  const worst = Math.max(aPct, mPct);

  const accent = worst >= 1 ? 'var(--color-danger)'
    : worst >= 0.8 ? 'var(--color-warning)'
    : 'var(--accent)';

  const Metric = ({ label, used, cap, unit }) => (
    <div className="flex-1">
      <div className="flex items-baseline justify-between mb-1">
        <span className="font-mono text-[10px] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>
          {label}
        </span>
        <span className="text-xs font-mono font-medium" style={{ color: 'var(--text-secondary)' }}>
          {used} <span style={{ color: 'var(--text-faint)' }}>/ {cap} {unit}</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-input)' }}>
        <div className="h-full rounded-full transition-all"
             style={{ width: `${Math.min(100, (cap ? used / cap : 0) * 100)}%`, background: accent }} />
      </div>
    </div>
  );

  return (
    <div className="rounded-xl p-4 mb-5"
         style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-0.5 h-3 rounded-full flex-shrink-0"
             style={{ background: 'linear-gradient(180deg, var(--accent) 0%, transparent 100%)' }} />
        <h2 className="font-mono text-[10px] font-medium uppercase tracking-[0.2em]" style={{ color: 'var(--text-faint)' }}>
          This week's usage
        </h2>
      </div>
      <div className="flex gap-5">
        <Metric label="Analyses" used={analysisCount} cap={caps.weeklyAnalyses} unit="" />
        <Metric label="Audio" used={minutesUsed} cap={caps.weeklyMinutes} unit="min" />
      </div>
      <p className="text-[11px] mt-3" style={{ color: 'var(--text-faint)' }}>
        Resets {resetAtMs ? format(new Date(Number(resetAtMs)), 'MMM d') : 'weekly'}
        {worst >= 1 && (
          <span style={{ color: 'var(--color-danger)' }}> · limit reached — new analyses are paused until reset</span>
        )}
      </p>
    </div>
  );
}

export default function HistoryPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  const loadUsage = useCallback(() => {
    catalystFetch(`${API_BASE}/usage`, { method: 'GET' })
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (data?.usage) setUsage(data.usage); })
      .catch(() => { /* banner is non-critical — silently skip */ });
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { setLoading(false); return; }
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    loadUsage();
    catalystFetch(`${API_BASE}/sessions`, { method: 'GET' })
      .then(res => {
        if (res.status === 401) { setError('AUTHENTICATION_REQUIRED'); setLoading(false); return null; }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (data) setSessions(data.sessions || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error('[HistoryPage] Fetch error:', err);
        setError('FETCH_FAILED');
        setLoading(false);
      });
  }, [isAuthenticated, authLoading, loadUsage]);

  const handleDelete = useCallback(async (sessionId) => {
    if (!window.confirm('Delete this analysis? This cannot be undone.')) return;
    try {
      const res = await catalystFetch(`${API_BASE}/sessions/${sessionId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      loadUsage();
    } catch (err) {
      console.error('[HistoryPage] Delete error:', err);
      window.alert('Could not delete this analysis. Please try again.');
    }
  }, [loadUsage]);

  if (authLoading || loading) {
    return (
      <div className="text-center py-16 text-sm" style={{ color: 'var(--text-faint)' }}>
        Loading sessions…
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="text-center py-16 space-y-2">
        <p className="text-4xl mb-3">🔒</p>
        <p className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>Login Required</p>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Please log in to view your session history.
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--text-faint)' }}>
          Click "Log In" in the header to get started.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16 space-y-2">
        <p className="font-semibold" style={{ color: 'var(--color-danger)' }}>Failed to load sessions.</p>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          {error === 'AUTHENTICATION_REQUIRED'
            ? 'Your session may have expired. Please log in again.'
            : 'Make sure the backend is running with FEATURE_PERSIST_SESSIONS=true.'}
        </p>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div>
        {usage && <UsageBanner usage={usage} />}
        <div className="rounded-xl p-10 text-center"
             style={{ background: 'var(--bg-surface)', border: '1px dashed var(--border-subtle)' }}>
          <p className="text-4xl mb-3">🎙️</p>
          <p className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>No analyzed speeches yet</p>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Upload or record a speech above to see it here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {usage && <UsageBanner usage={usage} />}
      <div className="space-y-3">
        {sessions.map(session => (
          <SessionCard key={session.session_id} session={session} onDelete={handleDelete} />
        ))}
      </div>
    </div>
  );
}
